import { useCallback, useEffect, useState } from 'react';

type UseTableObserverOptions = {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isScreenMobile: boolean;
};

type UseTableObserverReturn = {
  isScrolledToLeft: boolean;
  showFixedScrollbar: boolean;
  isTableBottomVisible: boolean;
};

/**
 * Hook to observe table scroll behavior and visibility
 * Tracks horizontal scroll position, scrollbar visibility, and table bottom visibility
 */
export const useTableObserver = ({
  scrollContainerRef,
  isScreenMobile,
}: UseTableObserverOptions): UseTableObserverReturn => {
  const [isScrolledToLeft, setIsScrolledToLeft] = useState(false);
  const [showFixedScrollbar, setShowFixedScrollbar] = useState(false);
  const [isTableBottomVisible, setIsTableBottomVisible] = useState(false);

  const checkTableBottomVisibility = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const tableRect = element.getBoundingClientRect();
    const isBottomVisible = tableRect.bottom <= window.innerHeight;
    setIsTableBottomVisible(isBottomVisible);
  }, [scrollContainerRef]);

  const handleTableScroll = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    const canScroll = element.scrollWidth > element.clientWidth && !isScreenMobile;
    const atLeft = element.scrollLeft === 0;

    setIsScrolledToLeft(!atLeft && canScroll);
    setShowFixedScrollbar(canScroll);
    checkTableBottomVisibility();
  }, [scrollContainerRef, isScreenMobile, checkTableBottomVisibility]);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element) return;

    element.addEventListener('scroll', handleTableScroll);
    window.addEventListener('resize', handleTableScroll);
    window.addEventListener('scroll', checkTableBottomVisibility);

    // Initial checks
    handleTableScroll();
    checkTableBottomVisibility();

    return () => {
      element.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('resize', handleTableScroll);
      window.removeEventListener('scroll', checkTableBottomVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleTableScroll, checkTableBottomVisibility]);

  return {
    isScrolledToLeft,
    showFixedScrollbar,
    isTableBottomVisible,
  };
};
